import { faEllipsisV, faRefresh } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    Alert,
    Button,
    ButtonGroup,
    Dropdown,
    DropdownButton,
    Form,
} from 'react-bootstrap'
import BarChart from '../../../common/charts/BarChart'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import LineChart from '../../../common/charts/LineChart'
import { FC, useCallback, useEffect, useState } from 'react'
import {
    DashboardCardProps,
    DashboardPayments,
    DashboardPaymentsChartType,
} from './types.d'
import { api } from '../../../../auth'

import { setDashboardChart } from '../../../../features/settings/settings.slice'
import dayjs from 'dayjs'

const statsPeriods = [
    { months: 3, label: 'Last 3 months' },
    { months: 6, label: 'Last 6 months' },
    { months: 9, label: 'Last 9 months' },
    { months: 12, label: 'Last 12 months' },
]

const chartTypes = [
    { code: 'bar', label: 'Bar Chart' },
    { code: 'line', label: 'Line Chart' },
]

const Payments: FC<DashboardCardProps> = () => {
    const theme = useAppSelector((state) => state.theme.theme)
    const dashboard = useAppSelector((state) => state.settings.dashboard)
    const dispatch = useAppDispatch()

    const [showPeriodsDropdown, setShowPeriodsDropdown] =
        useState<boolean>(false)
    const [selectedPeriod, setSelectedPeriod] = useState<number>(
        dashboard?.charts?.['payments']?.statsMonths || statsPeriods[1].months
    )

    const [widgetData, setWidgetData] = useState<DashboardPayments>()
    const [selectedChartType, setSelectedChartType] =
        useState<DashboardPaymentsChartType>(
            (dashboard?.charts?.['payments']
                ?.chartType as DashboardPaymentsChartType) || 'line'
        )

    const axiosPrivate = api
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')

    const updateChartData = useCallback(
        async (months: number, chartType: string = 'line') => {
            setErrMsg('')
            setIsLoading(true)

            try {
                const response = await axiosPrivate
                    .get(
                        `/finance/payments/stats/months/${months}?chartType=${chartType}`
                    )
                    .finally(() => {
                        setIsLoading(false)
                    })
                if (response.data?.success === true) {
                    setWidgetData(response.data.data.statsData)
                    setSelectedChartType(
                        chartType as DashboardPaymentsChartType
                    )
                    dispatch(
                        setDashboardChart({
                            ...dashboard,
                            charts: {
                                ...(dashboard?.charts || {}),
                                payments: {
                                    chartType,
                                    statsMonths: months,
                                },
                            },
                        })
                    )
                } else {
                    setErrMsg(response.data?.errors.join('<br />'))
                }
            } catch (err: any) {
                if (err?.response?.data?.errors) {
                    setErrMsg(err.response.data.errors.join('<br />'))
                } else {
                    if (!err?.response) {
                        setErrMsg('No Server Response')
                    } else if (err.response?.status === 404) {
                        setErrMsg('Record was not found')
                    } else {
                        setErrMsg('Failed to load dashboard')
                    }
                }
            }
        },
        [axiosPrivate]
    )

    useEffect(() => {
        updateChartData(selectedPeriod, selectedChartType)
    }, [updateChartData])

    return (
        <div className="chart-card">
            <div className="chart-card__header">
                <h5 className="chart-card__title">
                    <span
                        className="chart-card__title-icon"
                        style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                    >
                        <FontAwesomeIcon icon={['fas', 'credit-card']} />
                    </span>
                    Payments
                </h5>
                <span className="chart-card__period">
                    {statsPeriods.filter((p) => p.months === selectedPeriod)[0].label}
                </span>
                <ButtonGroup>
                    <Button
                        variant={theme}
                        size="sm"
                        onClick={() => {
                            updateChartData(selectedPeriod, selectedChartType)
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faRefresh}
                            size="sm"
                            spin={isLoading}
                        />
                    </Button>
                    <DropdownButton
                        title={
                            <FontAwesomeIcon
                                icon={faEllipsisV}
                                size="sm"
                                className="fa-fw"
                            />
                        }
                        id="paymentsStatsOptions"
                        variant={theme}
                        as={ButtonGroup}
                        className="no-arrow"
                        autoClose
                        show={showPeriodsDropdown}
                        onToggle={() => {
                            setShowPeriodsDropdown(!showPeriodsDropdown)
                        }}
                        align={'end'}
                        size="sm"
                    >
                        {statsPeriods.map((period) => (
                            <Dropdown.Item
                                key={period.months}
                                active={period.months === selectedPeriod}
                                onClick={() => {
                                    setSelectedPeriod(period.months)
                                    setShowPeriodsDropdown(false)
                                    updateChartData(
                                        period.months,
                                        selectedChartType
                                    )
                                }}
                            >
                                {period.label}
                            </Dropdown.Item>
                        ))}
                        <Form.Select
                            size="sm"
                            aria-label="Chart type"
                            defaultValue={selectedChartType}
                            className="dropdown-item"
                            onChange={(e) => {
                                setShowPeriodsDropdown(false)
                                updateChartData(selectedPeriod, e.target.value)
                            }}
                            data-toggle="dropdown"
                        >
                            {chartTypes.map((chartType) => (
                                <option
                                    key={chartType.code}
                                    value={chartType.code}
                                >
                                    {chartType.label}
                                </option>
                            ))}
                        </Form.Select>
                        {(widgetData as any)?.['last_updated'] && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Item disabled>
                                    <small>
                                        <span className="me-1">
                                            Last updated:
                                        </span>
                                        {dayjs(
                                            (widgetData as any)['last_updated']
                                        ).format('DD-MMM-YYYY @ HH:mm:ss')}
                                    </small>
                                </Dropdown.Item>
                            </>
                        )}
                    </DropdownButton>
                </ButtonGroup>
            </div>
            <div className="chart-card__body">
                {errMsg && (
                    <Alert show={true} variant="danger">
                        <Alert.Heading className="d-inline-block me-2">
                            Error!
                        </Alert.Heading>
                        {errMsg}
                    </Alert>
                )}
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 140 }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {(widgetData as any)?.['datasets']?.length > 0 ? (
                            <>
                                {(() => {
                                    switch (selectedChartType) {
                                        case 'line':
                                            return <LineChart data={widgetData as any} />
                                        case 'bar':
                                        default:
                                            return <BarChart data={widgetData as any} />
                                    }
                                })()}
                            </>
                        ) : (
                            <div
                                className="d-flex h-100 justify-content-center align-items-center text-muted"
                                style={{ minHeight: 140 }}
                            >
                                No data to display
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default Payments
